import {proofKey,requestedAttachment} from '../artwork-created/artworkCreatedModel.js'
import {recipientNeededIntent as makeRecipientNeededIntent} from '../workspace/recipientNeededIntent.js'
import { titleTierFor, titleTierLabel, titleTierEvidence, titleTierAllowsVariant } from '../../lib/titleTierView.js'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../lib/auth.jsx'
import {useToolAccess} from '../../lib/toolAccess.jsx'
import { approvedDossierCopy, senderSignatureFor, createDossierViewFence, unaddressedDraftKey, confirmedUnaddressedReceipt, dossierDraftFacts, verifiedSequenceDossier, dossierHoldScope, updateDossierHeld, updateDossierReceipt } from './dossierDraftReadiness.js'
import { api } from '../../lib/api.js'
import {outcomeDraftReference,recordConfirmedOutcome} from '../../lib/outreachOutcome.js'
import { useDossier } from './useDossier.js'
import { useDurableFacts } from './useDurableFacts.js'
import DurableFactEditor from './DurableFactEditor.jsx'
import { safeEvidenceUrl } from './durableFacts.js'
import { stageChampionCampaign, campaignDraftMarker } from './championCampaignDraft.js'
import {
  pricingDeepLink, campaignPayload, outreachReady,
} from '../../lib/championToClose.js'
import { listBuilds } from '../artwork/buildApi.js'
import { workspaceApi } from '../workspace/workspaceApi.js'
import { championEngineArtwork, artworkStudioLink, outreachArtwork } from './championArtwork.js'
import {outreachArtworkContext} from './outreachArtworkContext.js'
import { formatCountdown } from '../../lib/championships.js'

/* =====================================================================
 * THE ARTWORK REQUEST PANEL IS GONE FROM HERE, AND IT IS NOT COMING BACK.
 *
 * Brian, 2026-10-15: "the open the full dossier shows an artwork request
 * panel that is useless. Shouldn't live there. We have a place for art."
 *
 * WHAT IT WAS. Five SurfaceCards (bezel / side 1 / side 2 / palmside /
 * core), each with Generate proposal -> Render -> Approve/Revise, a
 * per-surface art-direction box, a GPT bridge for pasting an image back
 * from the Custom GPT, and a "Render all" button. Render was real: it
 * POSTed /api/artwork/render and created a durable draft build record.
 *
 * WHY IT WAS USELESS, on the evidence rather than on taste:
 *   - Image spend is centrally OFF (sr-portal-backend/spend.js,
 *     SR_ALLOW_IMAGE_SPEND, default off and correctly so), so the render
 *     route answers with a build record and NO IMAGE. Every artwork build
 *     on the production index reports imageCount: 0 -- not some, all.
 *   - The render route reports no markSource, so every surface resolved to
 *     drawn-by-model and isAttachableSurface was false. Nothing this panel
 *     could produce was ever permitted to be attached, exported or sent.
 *     The card said so itself: "Approved for internal review - not
 *     attachable".
 *   - So the panel's entire achievable output was a prompt string and a
 *     withheld proof -- and approving all five of them was the ONLY key to
 *     the Presentation Price and Stage Campaign gates below. A mandatory
 *     ritual with no product, standing between a rep and the price.
 *
 * It was also the SECOND surface in the app that could request a build.
 * The standing rule is ONE place for art, and the Artwork Studio is that
 * place: the sourced build spine, the locked layer (SR mark, the client's
 * OWNED crest, the typed title), the Marks Library, owned element upload,
 * and the human-gated draft -> proof -> submitted -> production ladder.
 * None of that existed here.
 *
 * WHAT REPLACES IT: a READ-ONLY status of the artwork that actually exists
 * for this championship, read from the build index, plus one door into the
 * Studio. No second form. No stub.
 * ===================================================================== */
import OutreachSequence from './OutreachSequence.jsx'
import OutreachArtwork from './OutreachArtwork.jsx'
/* REUSED, not reimplemented. Who the message is addressed to, how it is deduped
 * and how the tier redirect applies are decided once, server-side, in
 * outreach-recipients.js. A second copy of those rules on this screen would
 * drift, and the failure mode of drift in this particular file is an email to
 * the wrong person. */
import OutreachPicker from '../integrity/OutreachPicker.jsx'
import CoachCandidates from './CoachCandidates.jsx'
import {contactRecordBinding} from '../../lib/currentChampionshipContacts.js'
import CurrentChampionshipContacts from './CurrentChampionshipContacts.jsx'
import {confirmedSelectedCoachStage,confirmedSelectedCoachReceipt} from './coachCandidates.js'
import {getSessionGeneration} from '../../lib/sessionGeneration.js'
import {createDraftOperationRegistry,confirmedManualDraftReceipt} from './draftOperationIdentity.js'
import {manualRecipientIntent} from '../../lib/manualRecipientIntent.js'
import RepGmailConnect from './RepGmailConnect.jsx'
import {confirmedDossierProofResponse} from '../workspace/proofDraftReceipt.js'
import {useSessionGeneration} from '../../lib/useSessionGeneration.js'
import {OUTREACH_FAMILIES,buildFamilySequence,sequenceContextBinding,validSequenceProof} from '../../lib/outreachFamily.js'
import {useOutreachSequenceContext} from './useOutreachSequenceContext.js'
import SavedArtworkComposer from '../workspace/SavedArtworkComposer.jsx'
import styles from './championToClose.module.css'

function isDemo() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

/**
 * The Champion-to-Close dossier panel. Read-only assembly + local staging of
 * proposed art / price / campaign. Human gate on every surface; price gated
 * behind all-surfaces-approved; campaign gated behind price accepted.
 */
export default function ChampionToClose(props) {
  const session=useSessionGeneration()
  // A new login must reread facts, contacts, Gmail and artwork. The opaque
  // durable operation registry survives this reset; pending work is not canceled.
  return <SessionChampionToClose key={JSON.stringify([session,props.champ?.id,proofKey(props.initialArtworkProof)])} {...props}/>
}
function SessionChampionToClose({ champ, onClose, returnTo = '', initialSection = 'overview', initialArtworkProof = null }) {
  const demo = isDemo()
  const { user } = useAuth()
  const toolAccess = useToolAccess()
  const location = useLocation()
  const senderSignature = useMemo(() => senderSignatureFor(user), [user])
  const sender = useMemo(() => senderSignature ? { id: senderSignature.userId, name: senderSignature.name } : null, [senderSignature])
  const facts = useDurableFacts(champ, demo)
  const { dossier, setDossier, enrichStatus } = useDossier(facts.record)
  const copyDossier = useMemo(() => verifiedSequenceDossier(facts.raw, champ?.id), [facts.raw, champ?.id])
  const titleRouting = titleTierFor(facts.record)
  const ringEligible = titleRouting.ringArtworkEligible
  const titleBinding = JSON.stringify(titleTierEvidence(facts.record))
  const [campaignAngle,setCampaignAngle]=useState('')
  const [sequenceComposer,setSequenceComposer]=useState(null)
  const sequenceAngle=titleRouting.titleTier==='congrats_tier'?'regional-district-congratulations':campaignAngle
  const [factNote, setFactNote] = useState(null)
  const currentRecord = useRef('')
  currentRecord.current = String(champ?.id || '')
  /* THE ARTWORK THAT EXISTS, read from the build index. Never written from
   * here -- this panel is read-only on artwork now, the way it has always
   * claimed to be read-only on the championship universe. */
  const [artwork, setArtwork] = useState({ state: 'idle', rows: [] })
  const [engineJobs, setEngineJobs] = useState({ state: 'idle', rows: [] })
  const [engineRefresh, setEngineRefresh] = useState(0)
  const [price, setPrice] = useState({ opened: false })
  const [campaign, setCampaign] = useState({ drafted: false, staged: false })
  const autoFired = useRef(null)

  // The detection engine flags a crowned champion ready_for_artwork → the whole
  // dossier auto-assembles. Falls back to `crowned` until that engine lands.
  const shouldAutoAssemble = !!(champ && (champ.readyForArtwork || champ.crowned))

  // Can this actually be put in front of a customer? Computed from the dossier
  // rather than from surface approvals, because approving six pictures says
  // nothing about whether the WORDS are real.
  const outreach = useMemo(() => outreachReady(dossier), [dossier])

  // Reset when a different championship opens.
  useEffect(() => {
    setArtwork({ state: 'idle', rows: [] })
    setPrice({ opened: false })
    setCampaign({ drafted: false, staged: false })
    // Per-record, emphatically. Carrying "sent" across records would show the
    // next school's congratulation as already sent and it would never go out.
    setCampaignNote('')
    setFactNote(null)
  }, [champ?.id])

  // Prepare the internal campaign draft state once the dossier is enriched.
  // Quotes are created and approved in Pricing, never by opening its link.
  useEffect(() => {
    if (!shouldAutoAssemble || !dossier || enrichStatus === 'loading') return
    if (autoFired.current === champ.id) return
    autoFired.current = champ.id
    setCampaign({ drafted: true, staged: false })
  }, [shouldAutoAssemble, dossier, enrichStatus, champ?.id])

  /* ---- WHAT ARTWORK ACTUALLY EXISTS FOR THIS CHAMPIONSHIP -------------
   *
   * GET /api/artwork/builds is the SHARED list route (see buildApi.js) and
   * every index row carries its own championshipId, so this is one read and
   * a filter -- no new endpoint, no write, nothing staged.
   *
   * A failure here is reported, not swallowed into "no artwork". The two
   * are completely different answers for a rep deciding whether to go and
   * build the ring, and a silent empty state would send them to build a
   * second one.
   */
  useEffect(() => {
    const id = champ?.id
    if (!id) return
    if (demo) { setArtwork({ state: 'ready', rows: [] }); return }
    let live = true
    setArtwork({ state: 'loading', rows: [] })
    listBuilds({ limit: 200 })
      .then((res) => {
        if (!live) return
        const all = Array.isArray(res?.builds) ? res.builds : []
        const rows = all.filter((b) => String(b?.championshipId || '') === String(id))
        setArtwork({ state: 'ready', rows })
      })
      .catch((err) => {
        if (!live) return
        setArtwork({ state: 'error', rows: [], error: err?.message || 'the build list is unavailable' })
      })
    return () => { live = false }
  }, [champ?.id, demo])



  // Independent durable engine records; never substitute a brief for artwork
  // or treat internal visual review as customer/manufacturing approval.
  useEffect(() => {
    if (!champ?.id) return
    let live = true
    const controller = new AbortController()
    const refresh = () => {
      setEngineJobs(previous => ({ ...previous, state: 'loading' }))
      workspaceApi.jobs({}, { signal: controller.signal }).then(response => {
        if (live) setEngineJobs({ state: 'ready', rows: response.jobs || [] })
      }).catch(error => { if (live && error.name !== 'AbortError') setEngineJobs({ state: 'error', rows: [], error: error.message }) })
    }
    refresh()
    window.addEventListener('focus', refresh)
    return () => { live = false; controller.abort(); window.removeEventListener('focus', refresh) }
  }, [champ?.id, engineRefresh])
  const linkedEngine = useMemo(() => championEngineArtwork(engineJobs.rows, facts.record, returnTo), [engineJobs.rows, facts.record, returnTo])
  const attachmentContext = useMemo(() => outreachArtworkContext(engineJobs.rows, facts.record, returnTo), [engineJobs.rows, facts.record, returnTo])
  const attachmentOptions = useMemo(() => engineJobs.state === 'ready' ? outreachArtwork(engineJobs.rows, facts.record) : [], [engineJobs, facts.record])
  const requestedArtworkKey=proofKey(initialArtworkProof)
  const [attachmentKey, setAttachmentKey] = useState(initialArtworkProof?'requested':'auto')
  const [attachmentReadyKey, setAttachmentReady] = useState(null)
  const selectedAttachment = attachmentKey === 'requested' ? requestedAttachment(attachmentOptions,initialArtworkProof) : attachmentKey === 'auto'
    ? attachmentOptions.length === 1 ? attachmentOptions[0] : null
    : attachmentOptions.find(row => row.key === attachmentKey) || null
  const attachmentReady = Boolean(selectedAttachment && attachmentReadyKey === selectedAttachment.key)
  const [gmail, setGmail] = useState(null)
  const [draftBusy, setDraftBusy] = useState(false)
  const [heldByScope, setHeldByScope] = useState({})
  const holdScope = dossierHoldScope({ userId: user?.id, mailbox: gmail?.grant?.email, championshipId: champ?.id })
  const heldDrafts = heldByScope[holdScope] || []
  const setHeldDrafts = update => setHeldByScope(previous => updateDossierHeld(previous, holdScope, update))
  const draftLock = useRef(false)
  const draftOperations=useRef(null)
  if(!draftOperations.current)draftOperations.current=createDraftOperationRegistry()
  const campaignStages = useRef(new Map())
  const campaignStageScope = useRef('')
  const stageScope = JSON.stringify([champ?.id, facts.revision, titleBinding])
  // Replace, do not clear, the old map. An in-flight stage can finish after a
  // fact revision or navigation, but must not populate the new view's cache.
  // Render-time invalidation also closes the gap before reset effects run.
  if (campaignStageScope.current !== stageScope) {
    campaignStages.current = new Map()
    campaignStageScope.current = stageScope
  }
  const outreachRef = useRef(null)
  useEffect(() => { setAttachmentKey(initialArtworkProof?'requested':'auto') }, [champ?.id,requestedArtworkKey,initialArtworkProof])
  const dossierId = dossier?.championshipId
  const [platformHistory,setPlatformHistory]=useState(null)
  useEffect(()=>{
    let active=true
    setPlatformHistory(null)
    if(dossierId&&!demo)api.outreachHistory(dossierId).then(data=>{
      if(active)setPlatformHistory(data?.ok&&data.scope==='platform-recorded-only'&&data.provenance?.complete&&Array.isArray(data.events)?{id:dossierId,...data}:{id:dossierId,unavailable:true})
    }).catch(()=>{if(active)setPlatformHistory({id:dossierId,unavailable:true})})
    return()=>{active=false}
  },[dossierId,demo])
  useEffect(() => {
    if (initialSection !== 'outreach' || !dossierId) return undefined
    const frame = requestAnimationFrame(() => outreachRef.current?.scrollIntoView({block:'start'}))
    return () => cancelAnimationFrame(frame)
  }, [initialSection, dossierId])


  // PRICING seam — deep-link the record into the live pricing engine, pre-filled.
  const openPricing = () => {
    if (!ringEligible) return
    window.open(pricingDeepLink(dossier), '_blank', 'noopener,noreferrer')
    setPrice({ opened: true })
  }

  // OUTREACH seam — persist the campaign before creating a Gmail draft.
  const [campaignNote, setCampaignNoteValue] = useState('')
  const campaignNoteView = useRef(null)
  const setCampaignNote = note => { campaignNoteView.current = outboundBinding.current; setCampaignNoteValue(note) }

  /* SENDING ONE EMAIL, not the whole sequence.
   *
   * Tracked per-variant so a rep can send the day-0 congratulation today and
   * decide about the rest later — which is how a sequence is actually worked.
   * A single "sent" boolean for the whole campaign would force the choice to
   * be all-or-nothing and push everyone back to the generic blast.
   *
   * The staged payload carries the COMPOSED SUBJECT AND BODY, not just a
   * template id. What was reviewed on screen is exactly what is queued —
   * otherwise the rep approved one thing and the engine sends another. */
  // Facts come only from a direct record read. Never optimistically patch a
  // typed value into outbound copy, and never associate a typed name with the
  // previous coach's address or directory identity.
  const onFactResult = (result, recordId) => {
    if (String(recordId) !== currentRecord.current) return
    if (result.raw && String(result.raw.id) !== currentRecord.current) return
    setFactNote({ recordId: String(recordId), message: result.message + (result.state === 'saved' ? ' Existing drafts and artwork were not updated or reapproved; review them separately against these facts.' : '') })
  }

  const [receiptsByView, setReceiptsByView] = useState({})
  const recordDossierReceipt = (view, key, status) => setReceiptsByView(previous => updateDossierReceipt(previous, view, key, status))
  const unaddressedAttempts = useRef(new Map())
  const selectedCoachAttempts = useRef(new Map())
  const outcomeDrafts=useRef(new Map())
  const outcomeRequests=useRef(new Map())
  // Historical acknowledgement only. It cannot restore any draft/report action
  // or bypass the outbound authority fence after current evidence refreshes.
  const [reportedByView,setReportedByView]=useState({})
  const reportDisplayBinding=useRef('')
  const reportDisplayFence=useRef(createDossierViewFence())

  /* Who the rep has ticked in the picker below. Held here because the SEQUENCE
   * sends and the PICKER chooses, and until now those two halves of the screen
   * had no connection at all — the sequence addressed `dossier.coach.email` and
   * the picker's selection went nowhere near it. Empty by default: nobody is
   * ever pre-selected, which is the same rule the picker enforces. */
  const [legacySelection, setSelection] = useState({ mode: 'group', primaryKey: null, people: [] })
  const [candidateChoice,setCandidateChoice]=useState(null)
  const chooseCandidate=useCallback(choice=>{
    setSelection({mode:'group',primaryKey:null,people:[]})
    setCandidateChoice(choice)
  },[])
  const candidateView=JSON.stringify([champ?.id,contactRecordBinding(facts.record),facts.revision,titleBinding,location.key,location.pathname,location.search,location.hash,user?.id,gmail?.grant?.email,gmail?.grant?.connected])
  const chosenCandidate=!demo&&facts.ready&&!facts.busy&&candidateChoice?.viewKey===candidateView&&candidateChoice.sessionGeneration===getSessionGeneration()?candidateChoice:null
  const selection=chosenCandidate?{mode:'group',primaryKey:chosenCandidate.person.email,people:[chosenCandidate.person]}:legacySelection
  const sequenceRecipient=chosenCandidate?{contactId:chosenCandidate.selectedCoach.contactId,email:chosenCandidate.person.email}:null
  const sequenceRead=useOutreachSequenceContext({championshipId:String(champ?.id||''),angle:sequenceAngle,proof:selectedAttachment?.proof||null,recipient:sequenceRecipient,sender,enabled:facts.ready&&!facts.busy&&!!copyDossier&&!demo,refreshKey:facts.revision})
  const sequenceContext=sequenceRead.state==='ready'?sequenceRead.context:null
  const familySequence=useMemo(()=>buildFamilySequence(sequenceContext),[sequenceContext])
  const onSelectionChange = useCallback((s) => setSelection(s), [])
  const factView = useRef('')
  factView.current = JSON.stringify([champ?.id, facts.revision])
  const nativeArtworkBinding=useRef(''),nativeArtworkFence=useRef(createDossierViewFence())
  const outboundBinding = useRef('')
  const outboundFence = useRef(createDossierViewFence())
  useEffect(() => {
    const fence = outboundFence.current, displayFence = reportDisplayFence.current, nativeFence=nativeArtworkFence.current
    nativeArtworkBinding.current=nativeFence.open()
    outboundBinding.current = fence.open()
    reportDisplayBinding.current = displayFence.open()
    return () => { nativeFence.close();nativeArtworkBinding.current='unmounted';fence.close(); outboundBinding.current = 'unmounted'; displayFence.close(); reportDisplayBinding.current = 'unmounted' }
  }, [])
  outboundBinding.current = outboundFence.current.observe(JSON.stringify([champ?.id, facts.revision, titleBinding, facts.ready, facts.busy, selection, dossier?.coach?.email, selectedAttachment?.key, attachmentReady, senderSignature, gmail?.grant?.email, gmail?.grant?.connected, facts.raw, chosenCandidate?.selectedCoach, sequenceAngle, sequenceContext?.binding, location.key, location.pathname, location.search, location.hash]))
  nativeArtworkBinding.current=nativeArtworkFence.current.observe(JSON.stringify([getSessionGeneration(),champ?.id,facts.revision,titleBinding,facts.ready,facts.busy,facts.raw,chosenCandidate?.selectedCoach,chosenCandidate?.person.email,selectedAttachment?.proof,attachmentReady,senderSignature,gmail?.grant?.email,gmail?.grant?.connected,location.key,location.pathname,location.search,location.hash]))
  reportDisplayBinding.current = reportDisplayFence.current.observe(JSON.stringify([getSessionGeneration(), toolAccess?.data?.active, toolAccess?.data?.version, toolAccess?.data?.fingerprint, toolAccess?.data?.tools, champ?.id, facts.revision, titleBinding, facts.ready, facts.busy, selection, dossier?.coach?.email, selectedAttachment?.key, attachmentReady, senderSignature, gmail?.grant?.email, gmail?.grant?.connected, facts.raw, chosenCandidate?.selectedCoach, sequenceAngle, location.key, location.pathname, location.search, location.hash]))
  useEffect(() => {
    setCampaign({ drafted: false, staged: false })
    setSelection({ mode: 'group', primaryKey: null, people: [] })
    setCampaignNote(''); setAttachmentReady(null)
  }, [champ?.id, facts.revision])


  /* SELECT THE SCHOOL FROM THE SYSTEM — Brian's own words. These candidates are
   * real accounts the resolver found and REFUSED to choose between: a short
   * name that is a family of schools, or a single match in the wrong state.
   * Re-resolve pinned to the one the rep picked — a school KEY is matched
   * exactly by findCompany, so passing it ends the ambiguity rather than
   * re-entering it. Falls back to the full company name for a record with no
   * key yet. */
  const pickSchool = useCallback(async (cand) => {
    const pin = cand?.key || cand?.name
    if (!pin) return
    const view = factView.current
    setDossier((d) => d && { ...d, schoolCandidates: null, coachReview: null })
    try {
      const r = await api.coach({ school: pin, sport: champ?.sport || '',
        level: champ?.level || '', segment: champ?.segment || '', state: champ?.state || '' })
      if (view !== factView.current) return
      setDossier((d) => d && { ...d,
        committee: r.committee ?? null, suggestions: r.suggestions ?? null, reach: r.reach ?? null,
        peopleCount: r.peopleCount ?? null, resolvedSchool: r.school || null,
        schoolCandidates: r.candidates || null, coachReview: r.needs_review || null,
        ...(!facts.record?.ctcFactsVerified && r.coach?.name ? { coach: { name: r.coach.name, role: r.coach.role || 'Head Coach',
          email: r.coach.email || '', source: 'directory-picked', confidence: r.coach.confidence ?? 1 } } : {}),
      })
    } catch (err) {
      if (view !== factView.current) return
      /* Put the choice back rather than leaving an empty panel, which would look
       * like the pick worked and found nobody. */
      setDossier((d) => d && { ...d, schoolCandidates: [cand],
        coachReview: { reason: 'pick_failed',
          detail: `Could not re-read ${cand.name} just now (${err?.status || 'no endpoint'}). This is not a school with nobody at it — try again.` } })
    }
  }, [champ, setDossier, facts.record?.ctcFactsVerified])
  /* The rep's own Gmail connection, lifted here so the sequence panel can
   * disable the draft button and NAME the mailbox instead of failing at click. */

  /* =====================================================================
   * DRAFT IT INTO THE REP'S OWN MAILBOX.
   *
   * ⚠️ TWO CALLS, AND THE SECOND ONE IS THE POINT.
   *
   * This used to be `outreachStage` alone, and the button said "Send this one".
   * Staging writes the copy into the campaign record and logs the effort event;
   * it does not put an email anywhere. So the rep pressed a button labelled Send,
   * got a tick labelled Sent, and no mailbox on earth had anything in it. That is
   * the worst shape a send path can have, and it is the same fault the sequence
   * panel's own header warns about one level up.
   *
   * Now: stage (the record), then draft into the rep's own Gmail (the mailbox).
   *
   * Both calls must succeed before the row is marked drafted. A failed or
   * uncertain stage stops here: no durable receipt means no mailbox mutation,
   * and there is no local queue. Draft failures are reported, never hidden.
   * The draft call's three real
   * failures are all things the rep has to act on and all of them arrive with the
   * server's own sentence:
   *   409 recipient_already_claimed  another rep is already working this coach
   *   422 placeholder_copy_refused   the copy is not finished
   *   409 rep_not_connected          connect your Gmail first
   * Ticking any of those as "drafted" would hide a duplicate, a placeholder, or
   * a missing mailbox behind a green mark — which is how all three got here.
   * ===================================================================== */
  // Native copy is a separate explicit action; no campaign template or angle
  // is needed. The existing composer re-reads the exact original and recipient.
  const nativeComposeBinding=nativeArtworkBinding.current,nativeComposeSession=getSessionGeneration()
  const openNativeArtworkComposer=()=>{
    if(nativeComposeBinding!==nativeArtworkBinding.current||!nativeArtworkFence.current.current(nativeComposeBinding)||nativeComposeSession!==getSessionGeneration())return
    if(demo||!facts.ready||facts.busy||!copyDossier||!chosenCandidate||chosenCandidate.sessionGeneration!==getSessionGeneration()||!senderSignature)return
    if(!selectedAttachment||!attachmentReady||!validSequenceProof(selectedAttachment.proof)||selectedAttachment.proof.championshipId!==String(champ.id))return
    setSequenceComposer({mode:'native-artwork',binding:nativeComposeBinding,proof:selectedAttachment.proof,variant:'native-artwork',recipientSelection:{...chosenCandidate.selectedCoach,expectedEmail:chosenCandidate.person.email}})
  }
  const draftOne = async (variant, recipientMode = 'addressed') => {
    if (!variant?.available || !facts.ready || facts.busy) return
    const currentVariant=familySequence.find(item=>item.key===variant.key)
    if(!sequenceContext||!currentVariant?.available||variant.subject!==currentVariant.subject||variant.body!==currentVariant.body||variant.sequenceContext?.binding!==sequenceContext.binding||variant.sequenceContext?.angle!==sequenceAngle){setCampaignNote('Refresh the current campaign purpose and evidence before drafting. No fallback campaign was selected.');return}
    if(candidateChoice&&!chosenCandidate){setCampaignNote('The selected coach context changed. Clear the selection and read current coaches before drafting.');return}
    if(initialArtworkProof&&attachmentKey==='requested'&&!selectedAttachment){setCampaignNote('The requested exact artwork version is unavailable. No draft was requested.');return}
    const binding = outboundBinding.current
    const operationSession=getSessionGeneration()
    const operationCurrent=()=>operationSession===getSessionGeneration()&&binding===outboundBinding.current&&outboundFence.current.current(binding)
    const selectedCoachFacts=chosenCandidate?dossierDraftFacts(facts.raw,String(champ.id)):null
    if(chosenCandidate&&chosenCandidate.sessionGeneration!==getSessionGeneration()){setCampaignNote('The signed-in session changed. Read current coaches again.');return}
    if(chosenCandidate&&!selectedCoachFacts){setCampaignNote('Reload the complete championship facts before selecting this coach for a draft.');return}
    if (!approvedDossierCopy(variant, senderSignature)) { setCampaignNote('Refresh the current representative name and review the exact generated copy before drafting.'); return }
    if (draftLock.current) return
    if (heldDrafts.includes(variant.key)) { setCampaignNote('Reconcile the existing draft attempt before retrying. Its provider outcome is not confirmed.'); return }
    if (selectedAttachment && !attachmentReady) { setCampaignNote('Wait for the selected original bytes to finish loading.'); return }
    if(chosenCandidate&&selectedAttachment){
      if(!validSequenceProof(selectedAttachment.proof)||proofKey(sequenceContext.target?.proof)!==proofKey(selectedAttachment.proof)||sequenceContext.recipient?.contactId!==chosenCandidate.selectedCoach.contactId||sequenceContext.recipient?.email!==chosenCandidate.person?.email){setCampaignNote('The exact campaign artwork or coach changed. Refresh current evidence before opening the draft.');return}
      setSequenceComposer({binding,proof:selectedAttachment.proof,variant:variant.key,selection:{angle:sequenceAngle,variant:variant.key,recipient:{contactId:chosenCandidate.selectedCoach.contactId,email:chosenCandidate.person.email}}})
      return // Same atomic composer re-reads exact stage/coach/proof; no legacy draft or stage POST.
    }
    if (!(variant.key === 'the_ring' && selectedAttachment) && !titleTierAllowsVariant(facts.record, variant.key)) {
      setCampaignNote(titleRouting.titleTierReason); return
    }
    const base = {...campaignPayload(dossier, []),angle:sequenceAngle,sequenceContext:sequenceContextBinding(sequenceContext)}
    const payload = {
      ...base,
      variant: variant.key,
      persona: variant.persona,
      dayOffset: variant.day,
      subject: variant.subject,
      body: variant.body,
    }
    if (demo) { recordDossierReceipt(binding, variant.key, 'demo'); setCampaignNote('demo · nothing was drafted'); return }

    /* ⚠️⚠️ THE RECIPIENTS GO WITH IT NOW. BOTH HALVES WERE MISSING THEM.
     *
     * Measured on GET /api/outreach/chain in production, 2026-10-16:
     *   staged 4 · recipients 0 · breaksAt step 6 "Recipients selected"
     * Every staged campaign in the portal carries an EMPTY recipient list,
     * because this payload never had one: `campaignPayload` composes the copy
     * and addresses nobody. The chain does not break at step 8 for want of a
     * Gmail grant — it breaks at step 6 for want of an addressee.
     *
     * And the mailbox half was worse, because it looked like it worked: `to`
     * was `[dossier.coach.email]` and nothing else. One person, the head coach,
     * on every email in a six-step sequence — including the day-12 "working
     * back from your date" and the day-18 displacement, which are asks that
     * only the person holding the budget can answer. Brian, repeatedly: "Head
     * coaches a lot of the time are not decision makers, they might have final
     * say but they aren't doing championship ring designs, they just like to be
     * included." The AD, the athletic secretary and the business office were
     * all fetched, ranked and shown on the same screen, and could not be
     * written to.
     *
     * Worse again when the coach has no address: the row said "there is nobody
     * to draft to" at a school holding ten mailable people. That is the
     * confident false zero this codebase keeps hunting, in the one place it
     * stops a sale.
     *
     * The ticked selection is authoritative. The head coach is the FALLBACK,
     * and the note says when that is what happened, so a rep is never silently
     * writing to one person because they did not notice a list. */
    const ticked = (selection.people || []).filter((p) => p.email)
    const recipientIntent=chosenCandidate?null:manualRecipientIntent(base.championshipId,ticked)
    if(ticked.length&&!chosenCandidate&&!recipientIntent){setCampaignNote('Choose a verified coach or explicitly review a manual business address. Suggestions are never converted into manual recipients automatically.');return}
    const stagePayload = ticked.length
      ? { ...payload, mode: selection.mode || 'group', primaryKey: selection.primaryKey || null,
          recipients: ticked.map((p) => ({ name: p.name, email: p.email, role: p.role, title: p.title || '' })),
          ...(recipientIntent?{recipientIntent}:{}),
          ...(chosenCandidate?{selectionProtocol:'selected-coach-v1',selectedCoach:chosenCandidate.selectedCoach}:{}) }
      : payload

    // 2. the mailbox. A failure here is reported and NOT marked drafted.
    const to = ticked.length ? [ticked[0].email] : []
    const cc = ticked.length ? ticked.slice(1).map((p) => p.email) : []
    const neededArtwork=!to.length&&recipientMode==='unaddressed'&&!!selectedAttachment
    let artworkIntent=null
    if (!to.length&&!neededArtwork) {
      if (recipientMode !== 'unaddressed') { setCampaignNote('Choose the explicit save-without-recipient action to create a Recipient needed draft.'); return }
      if (selectedAttachment) { setCampaignNote('A recipient-free draft with artwork is held. Remove the attachment or select a verified recipient; existing proof approval does not authorize an unaddressed attachment.'); return }
      const request = { championshipId: base.championshipId, variant: variant.key, angle:base.angle, sequenceContext:base.sequenceContext, recipientMode: 'unaddressed',
        to: [], cc: [], bcc: [], subject: variant.subject, body: variant.body, senderSignature, facts: dossierDraftFacts(facts.raw, base.championshipId) }
      if (!request.facts) { setCampaignNote('Reload the complete current championship facts before saving a recipient-free draft.'); return }
      const attemptKey = unaddressedDraftKey({ ...request, signature: senderSignature, factRevision: facts.revision })
      if (unaddressedAttempts.current.has(attemptKey)) { setCampaignNote('This exact draft was already requested. Reconcile its saved outcome before trying again.'); return }
      let operation=null,blankRequested=false
      // Retain before awaiting: neither navigation nor an unknown provider result
      // permits this component to issue a second operation for the same copy.
      draftLock.current = true; setDraftBusy(true)
      try {
        operation=await draftOperations.current.begin({scope:holdScope,binding:request,current:operationCurrent})
        if(!operationCurrent())return
        request.requestId=operation.requestId
        unaddressedAttempts.current.set(attemptKey,request.requestId)
        blankRequested=true
        const result = await api.outreachDraftToGmail(request)
        if (!confirmedUnaddressedReceipt(result, request, gmail?.grant?.email)) throw new Error('No matching Recipient needed receipt was returned. Reconcile the saved outcome before retrying.')
        draftOperations.current.finish(operation,'confirmed')
        if (!operationCurrent()) return
        recordDossierReceipt(binding, variant.key, 'recipient_needed')
        setCampaignNote(`${variant.label}: draft saved in ${result.mailbox}. Recipient needed. To, Cc and Bcc are empty; add a recipient and review it in Gmail. Nothing was sent.`)
      } catch (error) {
        if(operation){try{draftOperations.current.finish(operation,blankRequested?'uncertain':'not-submitted')}catch{}}
        if (!operationCurrent()) return
        if(!blankRequested){setCampaignNote(`${variant.label}: no Gmail draft was requested by this attempt. ${error?.message || 'Local draft admission was not confirmed.'}`);return}
        setHeldDrafts(keys => [...keys, variant.key])
        setCampaignNote(`${variant.label}: no confirmed new draft. ${typeof error?.body?.detail === 'string' ? error.body.detail : error?.message || 'The request did not return a confirmed receipt.'} Reconcile the saved outcome before retrying.${operation?.requestId||error.requestId ? ` Operation: ${operation?.requestId||error.requestId}.` : ''}`)
      } finally {
        if(!blankRequested&&operation){try{draftOperations.current.finish(operation,'not-submitted')}catch{}}
        if(!blankRequested)unaddressedAttempts.current.delete(attemptKey)
        draftLock.current = false
        // Busy reflects this component's one global mutex, not a record receipt.
        // No newer operation can start until this release; release cannot repaint copy.
        setDraftBusy(false)
      }
      return
    }
    if (recipientMode === 'unaddressed'&&!neededArtwork) { setCampaignNote('Recipient-free mode cannot discard selected addresses. Review the recipient selection first.'); return }
    if(neededArtwork&&(selection.people||[]).length){setCampaignNote('Held or suggested contacts cannot be changed into recipient-needed artwork. Complete the contact review first.');return}
    let selectedCoachRequestId=null,operation=null,selectedAttemptKey=null
    if(chosenCandidate){
      const attemptKey=JSON.stringify([holdScope,chosenCandidate.selectedCoach,variant.key,variant.subject,variant.body,to,cc,senderSignature])
      if(selectedCoachAttempts.current.has(attemptKey)){setCampaignNote('This exact selected-coach draft was already requested. Reconcile its saved outcome before trying again.');return}
      selectedAttemptKey=attemptKey
      selectedCoachAttempts.current.set(attemptKey,'pending-local-admission')
    }
    draftLock.current = true; setDraftBusy(true)
    let draftRequested = false
    try {
      if(neededArtwork){
        const preview=await workspaceApi.emailPreview(selectedAttachment.proof.workspaceJobId)
        if(!operationCurrent())return
        if(!preview.recipientNeededAvailable||!preview.scopeReady||preview.actorId!==senderSignature.userId||preview.mailbox!==gmail?.grant?.email)throw Error('Recipient-needed artwork approval is unavailable. No draft was requested.')
        artworkIntent=await makeRecipientNeededIntent({actorId:senderSignature.userId,jobVersion:selectedAttachment.jobVersion,proof:selectedAttachment.proof,subject:variant.subject,body:variant.body,senderSignature})
        Object.assign(stagePayload,{recipientMode:'recipient-needed',recipientNeededIntent:artworkIntent,variant:variant.key,recipients:[],to:[],cc:[],bcc:[]})
      }
      operation=await draftOperations.current.begin({scope:holdScope,binding:{sequenceContext:base.sequenceContext,...(neededArtwork?{recipientNeededIntent:artworkIntent}:{}),recipientMode:neededArtwork?'recipient-needed':selectedAttachment?'prepared-proof':chosenCandidate?'selected-coach':'manual-addressed',recipientIntent,selectedCoach:chosenCandidate?.selectedCoach||null,to,cc,subject:variant.subject,body:variant.body,senderSignature,facts:chosenCandidate?selectedCoachFacts:dossierDraftFacts(facts.raw,String(champ.id)),proof:selectedAttachment?.proof||null},current:operationCurrent})
      if(!operationCurrent())return
      if(chosenCandidate){selectedCoachRequestId=operation.requestId;Object.assign(stagePayload,{recipientMode:'selected-coach',facts:selectedCoachFacts,senderSignature,requestId:selectedCoachRequestId})}
      const staged=await stageChampionCampaign({stage:api.outreachStage,payload:stagePayload,cache:campaignStages.current})
      if (!operationCurrent()) return
      if(chosenCandidate&&chosenCandidate.sessionGeneration!==getSessionGeneration())return
      if(chosenCandidate&&!confirmedSelectedCoachStage(staged,chosenCandidate.selectedCoach,selectedCoachRequestId))throw new Error('The server did not confirm the selected coach identity. No Gmail draft was requested.')
      const campaignMarker = campaignDraftMarker(staged,selectedAttachment?.proof)
      draftRequested = true
      const r = await api.outreachDraftToGmail({
        championshipId: base.championshipId,
        variant: variant.key,
        requestId:operation.requestId,
        ...(selectedAttachment ? { artworkProof: selectedAttachment.proof } : {}),
        ...(neededArtwork?{recipientMode:'recipient-needed',recipientNeededIntent:artworkIntent,bcc:[]}:{}),
        angle: base.angle || null,
        sequenceContext:base.sequenceContext,
        campaignId: staged.campaignId,
        ...(recipientIntent?{recipientIntent,manualCampaignBinding:{required:true,campaignId:staged.campaignId,version:staged.version}}:{}),
        ...(chosenCandidate?{selectionProtocol:'selected-coach-v1',selectedCoach:chosenCandidate.selectedCoach,requestId:selectedCoachRequestId,recipientMode:'selected-coach',facts:selectedCoachFacts,bcc:[],campaignBinding:{required:true,campaignId:staged.campaignId,version:staged.version}}:{}),
        ...campaignMarker,
        to,
        cc,
        subject: variant.subject,
        body: variant.body,
        senderSignature,
      })
      if(chosenCandidate&&chosenCandidate.sessionGeneration!==getSessionGeneration())return
      if(chosenCandidate&&!await confirmedSelectedCoachReceipt(r,{selectedCoach:chosenCandidate.selectedCoach,requestId:selectedCoachRequestId,mailbox:gmail?.grant?.email,repId:senderSignature.userId,campaignId:staged.campaignId,campaignVersion:staged.version,to,subject:variant.subject,body:variant.body}))throw new Error('No matching selected-coach receipt was returned. Reconcile the draft outcome before retrying.')
      if(chosenCandidate&&chosenCandidate.sessionGeneration!==getSessionGeneration())return
      if(!chosenCandidate&&!selectedAttachment&&!await confirmedManualDraftReceipt(r,{requestId:operation.requestId,mailbox:gmail?.grant?.email,to,cc,repId:senderSignature.userId,championshipId:base.championshipId,subject:variant.subject,body:variant.body,recipientIntent,campaignId:staged.campaignId,campaignVersion:staged.version}))throw new Error('No matching manual-addressed draft receipt was returned. Reconcile its saved outcome before retrying.')
      if (!operationCurrent()) return
      if(selectedAttachment&&!await confirmedDossierProofResponse(r,{...(neededArtwork?{recipientMode:'recipient-needed',recipientNeededIntent:artworkIntent}:{}),recipientIntent,requestId:operation.requestId,actorId:senderSignature.userId,mailbox:gmail?.grant?.email,proof:selectedAttachment.proof,purpose:selectedAttachment.purpose,to,cc,subject:variant.subject,body:variant.body,attachmentName:selectedAttachment.customerFilename,attachmentBytes:selectedAttachment.output?.bytes,requestCampaign:{campaignId:staged.campaignId,version:staged.version,outreachStep:variant.key}}))throw new Error('No matching draft and attachment receipt was returned. Check saved draft status before retrying.')
      if (!operationCurrent()) return
      if (!r.draftId) throw new Error('No confirmed draft receipt was returned.')
      draftOperations.current.finish(operation,'confirmed')
      if (!operationCurrent()) return
      recordDossierReceipt(binding, variant.key, neededArtwork?'recipient_needed':'addressed')
      if(!neededArtwork)outcomeDrafts.current.set(JSON.stringify([binding,variant.key]),outcomeDraftReference(r,selectedAttachment?.proof))
      if(neededArtwork){setCampaignNote(`${variant.label}: exact artwork draft saved in ${r.mailbox}. Needs recipient. To, Cc and Bcc are empty. Add and review the recipient in Gmail; nothing was sent.`);return}
      /* WHO it went to, named. A rep who did not notice the picker should learn
       * that from the confirmation, not from a reply that never comes. */
      const who = ticked.length
        ? `${ticked[0].name}${cc.length ? ` (copying ${ticked.slice(1).map((p) => p.name).join(', ')})` : ''}`
        : `${dossier?.coach?.name || 'the head coach'} only`
      setCampaignNote(`${variant.label}: draft waiting in ${r.mailbox}, addressed to ${who}${selectedAttachment ? ` with ${r.attachment.name} attached` : ''}. Open Gmail and send it yourself.`
)
    } catch (err) {
      if(operation){try{draftOperations.current.finish(operation,draftRequested?'uncertain':'not-submitted')}catch{}}
      if (!operationCurrent()) return
      if (chosenCandidate && chosenCandidate.sessionGeneration !== getSessionGeneration()) return
      if (binding !== outboundBinding.current) return
      if (!draftRequested) {
        setCampaignNote(`${variant.label}: no Gmail draft was requested by this attempt. ${err?.message || 'Campaign save was not confirmed.'} Check saved operation and campaign history before retrying.${operation?.requestId||err.requestId ? ` Operation: ${operation?.requestId||err.requestId}.` : ''}`)
        return
      }
      if(!selectedAttachment||!err.status||err.status>=500||['draft_outcome_uncertain','draft_already_reserved','draft_receipt_pending'].includes(err.body?.error))setHeldDrafts(keys => [...keys, variant.key])
      setCampaignNote(`${variant.label}: no confirmed new draft. ${err?.body?.detail || err?.message || `The draft call failed (${err?.status || 'network'}).`} Reconcile before retrying.${operation?.requestId||err.requestId ? ` Operation: ${operation?.requestId||err.requestId}.` : ''}`)
    } finally {
      if(!draftRequested&&operation){try{draftOperations.current.finish(operation,'not-submitted')}catch{}}
      if(!draftRequested&&selectedAttemptKey)selectedCoachAttempts.current.delete(selectedAttemptKey)
      draftLock.current = false; setDraftBusy(false)
    }
  }
  /* The rep is the only witness to the send, so the rep records it. Throws on
   * failure so the button does NOT flip to "recorded" — an unrecorded send that
   * looks recorded is a hole in the only measurement step 9 has. */
  const confirmSent = async (variant) => {
    const base = campaignPayload(dossier, [])
    const operationSession=getSessionGeneration(),view=outboundBinding.current,displayView=reportDisplayBinding.current,key=JSON.stringify([view,variant.key])
    if (demo) { setCampaignNote('demo · nothing recorded'); throw new Error('demo') }
    try {
      const draft=outcomeDrafts.current.get(key)
      if(!draft)throw new Error('The saved draft reference is unavailable. Reload its receipt before recording an outcome.')
      if(!outcomeRequests.current.has(key))outcomeRequests.current.set(key,crypto.randomUUID())
      const receipt=await recordConfirmedOutcome({send:api.outreachOutcome,request:{kind:'sent',championshipId:base.championshipId,requestId:outcomeRequests.current.get(key),draft},actorId:senderSignature.userId,isCurrent:()=>operationSession===getSessionGeneration()&&view===outboundBinding.current&&displayView===reportDisplayBinding.current&&reportDisplayFence.current.current(displayView)})
      setReportedByView(previous=>({...previous,[displayView]:{...(previous[displayView]||{}),[variant.key]:{key:variant.key,label:variant.label||'This draft',receipt}}}))
      setCampaignNote(`Recorded your send report for ${variant?.label || 'this draft'} (receipt ${receipt.id}). This is your confirmation, not provider-verified delivery.`)
      sequenceRead.refresh()
    } catch (err) {
      if(operationSession===getSessionGeneration()&&view===outboundBinding.current&&displayView===reportDisplayBinding.current)setCampaignNote(`Send report not confirmed here: ${err.message}. Retry the same confirmation to reconcile its receipt.`)
      throw err
    }
  }

  const sendCampaign = async () => {
    if (!facts.ready || facts.busy || !canCampaign || draftLock.current) return
    const binding = outboundBinding.current
    if (demo) { setCampaign({ drafted: true, staged: true }); setCampaignNote('demo · engine call skipped'); return }
    draftLock.current = true; setDraftBusy(true)
    try {
      const staged = await api.outreachStage(campaignPayload(dossier, []))
      if (staged?.ok !== true || staged.status !== 'staged'
        || typeof staged.campaignId !== 'string' || !staged.campaignId.trim()
        || !Number.isInteger(staged.version) || staged.version < 1) throw new Error('campaign_save_unconfirmed')
      if (binding !== outboundBinding.current) return
      // This separate action changed the canonical campaign version. No
      // earlier per-variant receipt can authorize the next artwork draft.
      campaignStages.current = new Map()
      setCampaign((p) => ({ ...p, staged: true })); setCampaignNote('')
    } catch (err) {
      if (binding !== outboundBinding.current) return
      setCampaign((p) => ({ ...p, staged: false }))
      setCampaignNote(`The draft was not staged with a confirmed receipt (${err?.status || 'network'}). Check the saved campaign before retrying.`)
    } finally {
      draftLock.current = false; setDraftBusy(false)
    }
  }

  /* ---- THE PRICE GATE, RE-BASED ON REAL WORK --------------------------
   *
   * It used to be `allApproved(surfaces)` -- approve five pictures in this
   * panel and the price unlocked. Those five pictures are gone, so the gate
   * had to move or it would have locked for ever, and "remove the panel and
   * silently brick the price button" is a worse bug than the panel.
   *
   * It now reads a REAL signal from the REAL surface: at least one artwork
   * build recorded against this championship in the Studio. That preserves
   * what the gate was for -- do not price a ring nobody has specified --
   * while putting the specifying where it belongs. It cannot be satisfied
   * from inside this panel, which is the point.
   *
   * A failed build read does NOT open the gate. Fail closed.
   */
  const builds = artwork.rows
  const legacyPriceReady = artwork.state === 'ready' && builds.length > 0
  const enginePriceReady = engineJobs.state === 'ready' && linkedEngine.some(row => row.pricingEligible)
  // Durable fact editing does not rewrite or approve Studio QA. Engine pricing
  // uses server-supplied final-champion readiness, including its fact/proof
  // binding; legacy Studio pricing behavior remains a separate existing path.
  const canPrice = ringEligible && (legacyPriceReady || enginePriceReady)
  // This action saves a draft; opening Pricing establishes no quote approval.
  const canCampaign = canPrice
  const champion = dossier?.champion
  const c = dossier?.championship

  const enrichLabel = useMemo(() => ({
    idle: '', loading: 'enriching…', done: 'enriched', error: 'enrichment unavailable',
  }[enrichStatus] || ''), [enrichStatus])

  if (!champ) return null

  return (
    <div className={styles.overlay} onClick={onClose}>
      <aside className={styles.panel} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* Header */}
        <div className={styles.head}>
          <div>
            <div className="eyebrow">Champion to Close</div>
            <h2 className={styles.title}>{c?.name || champ.name}</h2>
            <div className={styles.sub}>
              {[c?.sport, c?.level, c?.conference].filter(Boolean).join(' · ') || '—'}
              {c?.effDate && <span> · {c.effDateLabel} · {formatCountdown(c.daysToEvent)}{c.estimated ? ' (est)' : ''}</span>}
            </div>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Auto-assembly banner */}
        {shouldAutoAssemble ? (
          <div className={styles.autoBanner}>
            <span className={styles.autoDot} />
            {/* ⚠️ IT NO LONGER CLAIMS TO HAVE PROPOSED ANY ARTWORK, because it
              * no longer does. The old copy said it "proposed all 5 ring
              * surfaces" — and the five proposals were local outlines that
              * could never become an attachable proof. A banner that announces
              * work the system cannot finish is the same defect as a
              * placeholder that reads like prose. It now states only what
              * actually happened: the brand kit, the price, the campaign. */}
            <span><strong>Dossier prepared.</strong> Check the sourced facts and contact choices. {ringEligible ? 'Build the ring in the Artwork Studio; create and approve its quote in the Pricing Engine.' : titleRouting.titleTierReason}</span>
          </div>
        ) : (
          <div className={styles.manualBanner}>
            No champion on this record yet — auto-assembly fires once one is recorded. You can compose manually below.
          </div>
        )}

        {/* Dossier */}
        <section className={styles.section}>
          <p role="status" data-title-tier={titleRouting.titleTier}><strong>{titleTierLabel(facts.record)}.</strong> {titleRouting.titleTierReason}</p>
          <div className={styles.sectionHead}><span className="eyebrow">Dossier</span>{enrichLabel && <span className={styles.enrich}>{enrichLabel}</span>}</div>
          <div className={styles.dossier}>
            <div className={styles.champBlock}>
              <div className={styles.champLogo}>
                {champion?.logoUrl ? <img src={champion.logoUrl} alt="" /> : <span>{(champion?.school || '?')[0]}</span>}
              </div>
              <div>
                <div className={styles.champSchool}>{champion?.school || <em className={styles.gap}>champion not captured</em>}</div>
                {champion?.mascot && <div className={styles.champMascot}>{champion.mascot}</div>}
                <div className={styles.swatches}>
                  {champion?.colors?.length
                    ? champion.colors.map((col, i) => <span key={i} className={styles.swatch} style={{ background: col.hex }} title={col.hex} />)
                    : <span className={styles.gapSm}>no verified colors</span>}
                  <span className={styles.srcTag}>{champion?.enrichment === 'hubspot' ? 'owned · HubSpot' : champion?.enrichment === 'record' ? 'from record' : 'missing'}</span>
                </div>
              </div>
            </div>
            <div className={styles.dossierRows}>
              <div className={styles.dRow}><span className={styles.dK}>Historical championship coach</span><div className={styles.dV}>
                {dossier?.coach?.name || <em className={styles.gapSm}>Historical championship coach not recorded</em>}{' '}
                <span className={styles.okTagSm}>{facts.ready ? 'championship fact · not a contact identity' : 'unverified snapshot · drafting blocked'}</span>
                {!dossier?.coach?.name && <span className={styles.stubTagSm}>This historical fact is blank. Current contact availability is shown separately below.</span>}
                <DurableFactEditor key={`${champ.id}:${facts.revision}:coach`} facts={facts} field="champion_head_coach" onResult={onFactResult} />
              </div></div>

              {dossier?.coachReview?.reason==='contact_lookup_failed'&&<p role="status" aria-label="Legacy school directory lookup">Legacy school directory contact lookup failed. Needs continued research. This does not establish that the school has no coaches or change the historical championship fact.</p>}
              <CurrentChampionshipContacts record={facts.record}/>

              {/* THE ENGRAVABLE FACTS. Shown as discrete rows rather than
                  buried in prose, because these are what goes on the ring and
                  a designer should not have to parse a paragraph for them.
                  Any line the source did not state is simply absent — no
                  dashes, no "TBD", nothing to mistake for a value. */}
              {dossier?.championship?.story?.titleLine &&
                <div className={styles.dRow}><span className={styles.dK}>Title line</span><span className={styles.dV}>{dossier.championship.story.titleLine}</span></div>}
              {dossier?.championship?.story?.score &&
                <div className={styles.dRow}><span className={styles.dK}>Final score</span><span className={styles.dV}>
                  {dossier.championship.story.score}
                  {dossier.championship.story.runnerUp && <> <span className={styles.dK} style={{ textTransform: 'none' }}>over {dossier.championship.story.runnerUp}</span></>}
                </span></div>}
              {dossier?.championship?.story?.record &&
                <div className={styles.dRow}><span className={styles.dK}>Record</span><span className={styles.dV}>{dossier.championship.story.record}</span></div>}
              {dossier?.championship?.story?.venue &&
                <div className={styles.dRow}><span className={styles.dK}>Venue</span><span className={styles.dV}>{dossier.championship.story.venue}</span></div>}

              <div className={styles.dRow}><span className={styles.dK}>Season story</span><div className={styles.dV}>
                {dossier?.seasonStory?.text || <em className={styles.gapSm}>no saved story yet</em>}{' '}
                <span className={styles.okTagSm}>{facts.ready ? 'from saved record · manual changes use the audit path' : 'snapshot · awaiting saved-record verification'}</span>
                <DurableFactEditor key={`${champ.id}:${facts.revision}:story`} facts={facts} field="champion_story" onResult={onFactResult} />
              </div></div>
              <div className={styles.dRow}><span className={styles.dK}>Story evidence</span><div className={styles.dV}>
                {safeEvidenceUrl(facts.raw?.champion_story_source) ? <a href={facts.raw.champion_story_source} target="_blank" rel="noreferrer">Recorded evidence URL ↗</a> : <span>No valid story evidence URL recorded. A champion source is not substituted.</span>}
                <DurableFactEditor key={`${champ.id}:${facts.revision}:source`} facts={facts} field="champion_story_source" onResult={onFactResult} />
              </div></div>
              {!facts.ready && <p role="status">{facts.status === 'error' ? `Saved facts could not be verified: ${facts.error}` : facts.status === 'demo' ? 'Demo: durable editing is disabled.' : 'Reading saved coach and story…'} Fact editing and sequence drafting stay blocked.</p>}
              {facts.status === 'error' && <button type="button" className="btn" onClick={facts.reload}>Reload saved facts</button>}
              {factNote?.recordId === String(champ.id) && <p role="status">{factNote.message}</p>}
            </div>
          </div>
        </section>

        {/* ---- RING ARTWORK: READ-ONLY, WITH ONE DOOR ------------------
          *
          * This is NOT a request form, and it must never become one again.
          * It states what artwork exists for THIS championship, read from
          * the build index, and sends a rep who needs art to the one place
          * art is made. See the block at the top of this file for what was
          * here before and the evidence for why it went.
          */}
        <section className={styles.section}>
          {ringEligible ? <>
          <div className={styles.sectionHead}>
            <span className="eyebrow">Ring artwork</span>
            <span className={styles.headActions}>
              <Link className={styles.studioLink} to={artworkStudioLink(champ, null, returnTo)}>Create artwork / open saved versions &rarr;</Link>
            </span>
          </div>

          <div aria-label="Linked Artwork Engine records">
            {engineJobs.state === 'loading' && <p className={styles.gateNote}>Checking saved Artwork Engine jobs…</p>}
            {engineJobs.state === 'error' && <p className={styles.gateNote} role="alert">Could not read saved Artwork Engine jobs: {engineJobs.error}. This is not evidence that no artwork exists.</p>}
            {linkedEngine.map(row => <article key={row.jobId} className={styles.gateNote} data-engine-job={row.jobId}>
              <strong>{row.title}</strong>
              <p>{row.status} · Engine revision {row.revision} · {row.scope === 'company' ? 'Account-linked; confirm the championship before pricing' : 'Linked by '+row.scope+' ID'}</p>
              {row.output ? <p>Saved original: {row.output.name}. {'Available for outbound preparation.'}</p> : <p>No generated output is saved for this revision. A brief is not a finished concept.</p>}

              <Link to={row.url}>Open saved Artwork Engine job →</Link>
            </article>)}
          </div>

          {artwork.state === 'loading' && (
            <p className={styles.gateNote}><span className="spinner" /> Checking what artwork exists for this championship...</p>
          )}

          {/* A FAILED READ IS NOT "NO ARTWORK". Collapsing the two would send
              a rep off to build a second ring for a school that already has
              one, which is the expensive version of this mistake. */}
          {artwork.state === 'error' && (
            <p className={styles.gateNote} role="note">
              <strong>Could not read the artwork builds for this record</strong> ({artwork.error}).
              That is not the same as none on file, so do not start a build on the strength of this screen.
              The Studio&apos;s hand-off stage lists every build.
            </p>
          )}

          {artwork.state === 'ready' && engineJobs.state === 'ready' && builds.length === 0 && linkedEngine.length === 0 && (
            <p className={styles.gateNote}>
              No ring artwork has been built for this championship yet. Build it in the Artwork Studio:
              the sourced build spine, the Marks Library and the client&apos;s owned crest live there, and
              the title and school are locked so nothing rewrites them. Artwork cannot be requested from
              this panel, by design.
            </p>
          )}

          {artwork.state === 'ready' && builds.length > 0 && (
            <>
              <p className={styles.gateNote}>
                {builds.length} artwork build{builds.length === 1 ? '' : 's'} on record for this championship.
                Read-only here &mdash; status moves in the Studio&apos;s hand-off stage, which is where the
                draft / proof / submitted / production ladder is gated.
              </p>
              <ul className={styles.gateNote}>
                {builds.slice(0, 6).map((b) => (
                  <li key={b.buildId}>
                    <strong>{b.status}</strong>
                    {b.product ? ` \u00b7 ${b.product}` : ''}
                    {b.style ? ` \u00b7 ${b.style}` : ''}
                    {b.title ? ` \u00b7 ${b.title}` : ''}
                    {' \u00b7 '}
                    {/* THE HONEST COUNT. Image spend is off, so this is 0 on
                        every build today. Printing "rendered" regardless would
                        be the exact lie the removed panel told. */}
                    {b.imageCount > 0
                      ? `${b.imageCount} view${b.imageCount === 1 ? '' : 's'} rendered`
                      : 'no rendered view yet'}
                    {' '}<span className={styles.stubTagSm}>{b.buildId}</span>
                  </li>
                ))}
              </ul>
              {builds.length > 6 && (
                <p className={styles.gateNote}>and {builds.length - 6} more &mdash; the full list is in the Studio.</p>
              )}
            </>
          )}
          </> : <p className={styles.gateNote}>{titleRouting.titleTier === 'congrats_tier' ? 'No ring artwork for this award. Use the congratulations sequence below without an artwork attachment.' : 'Ring artwork is held until the award level is reviewed.'}</p>}
        </section>

        {/* Gated: Presentation Price */}
        {ringEligible && <section className={styles.section}>
          <div className={`${styles.gateStage} ${canPrice ? styles.gateOpen : styles.gateLocked}`}>
            <div>
              <div className={styles.gateTitle}>{!canPrice && '🔒 '}Presentation Price</div>
              {/* THE LOCK NAMES THE REAL REASON AND THE REAL PLACE TO FIX IT.
                  "0/5 surfaces approved" pointed at a control that no longer
                  exists; a lock whose stated condition cannot be met anywhere
                  is how a rep concludes the tool is broken. */}
              <div className={styles.gateSub}>{
                canPrice
                  ? (enginePriceReady ? 'Current internally reviewed engine concept on record — open pricing to prepare an estimate. No customer or manufacturing approval is implied.' : `Ring artwork on record (${builds.length} build${builds.length === 1 ? '' : 's'}) — open the live pricing engine, pre-filled for this ring.`)
                  : artwork.state === 'loading' || engineJobs.state === 'loading'
                    ? 'Locked · checking for ring artwork…'
                    : artwork.state === 'error' || engineJobs.state === 'error'
                      ? 'Locked · an artwork source could not be read, so this stays closed'
                      : linkedEngine.length ? 'Locked · save and internally review the current linked engine output first; a brief or unreviewed concept does not open pricing' : 'Locked · no ring artwork built yet — build it in the Artwork Studio'
              }</div>
            </div>
            {!price.opened ? (
              <div className={styles.stageAction}>
                <span className={styles.stubTagSm}>live engine · deep-link</span>
                <button className="btn btn-primary" disabled={!canPrice} title={canPrice ? '' : 'Build the ring in the Artwork Studio first — this gate reads the build record, not an approval in this panel'} onClick={openPricing}>Open presentation price →</button>
              </div>
            ) : (
              <span className={styles.stageAction}><span className={styles.approvedFlag}>Opened in pricing engine</span><button className={styles.reopenBtn} onClick={openPricing}>Re-open →</button></span>
            )}
          </div>
        </section>}

        {/* Gated: Stage Campaign */}
        <section className={styles.section}>
          {ringEligible && <div className={`${styles.gateStage} ${canCampaign ? styles.gateOpen : styles.gateLocked}`}>
            <div>
              <div className={styles.gateTitle}>{!canCampaign && '🔒 '}Stage Campaign Draft</div>
              <div className={styles.gateSub}>Save an internal outreach draft. Approve the quote in the Pricing Engine before sending a priced offer.</div>
            </div>
            {!campaign.staged ? (
              <div className={styles.stageAction}>
                {/* THE COPY GATE. Everything above this point can be approved
                    while the story is still missing — and this button is what
                    puts words in front of a customer. It used to be reachable
                    with a season story that read "headline record, key wins,
                    and the title-clinching moment go here", which is a note to
                    a copywriter, not a sentence to send a coach.
                    Now it names exactly what is missing and refuses. */}
                {!outreach.ready && <span className={styles.stubTagSm}>needs {outreach.missing.join(' · ')}</span>}
                {outreach.ready && <span className={styles.stubTagSm}>outreach engine seam</span>}
                <button
                  className="btn btn-primary"
                  disabled={!canCampaign || !outreach.ready || !facts.ready || facts.busy || draftBusy}
                  title={!outreach.ready ? `Missing: ${outreach.missing.join(' · ')}` : canCampaign ? '' : 'Build the ring in the Artwork Studio first'}
                  onClick={sendCampaign}
                >Stage draft</button>
              </div>
            ) : (
              <span className={styles.stageAction}><span className={styles.approvedFlag}>Draft staged</span></span>
            )}
          </div>}
          {campaignNote && campaignNoteView.current === outboundBinding.current && <p className={styles.foot} role="status">{campaignNote}</p>}

          {/* THE SEQUENCE ITSELF.
            *
            * Brian: "there should be a button that allows for a very directed
            * email that is not generic. we should have sequencing tied in here
            * which shows multiple styles of outreach."
            *
            * It sits OUTSIDE the price gate on purpose. The gate above governs
            * sending a whole priced campaign; reading the drafts, copying one,
            * and sending a single warm congratulation on the day they won
            * should not wait on an accepted quote. Making the day-0 note wait
            * for pricing is how it arrives two weeks late, by which point it
            * is no longer a congratulation. */}
          {/* ⚠️ THE CONNECT CARD SITS ABOVE THE SEQUENCE, NOT IN SETTINGS.
              A rep discovers they need it at the moment they try to draft, so
              the control belongs where that moment happens. Tucked into a
              preferences page it is a feature fifteen people never find, and the
              drafts they cannot create look like the tool being broken. */}
          <RepGmailConnect onStatus={setGmail} />
          {dossier ? <>
          <div ref={outreachRef} />

          {/* ⚠️⚠️ WHO IT GOES TO — THE HALF THIS SCREEN DID NOT HAVE.
            *
            * Brian: "when a champion is named, we have the records of coaches
            * and who to contact, we should be able to select the school from
            * the system and pull the email record for the correct coach and
            * suggest the coaches to reach out to."
            *
            * There were TWO outreach front doors and each held exactly the half
            * the other was missing. THIS one — the crown dossier — had the six
            * composed angles with their days, and addressed every one of them
            * to `dossier.coach.email`: the head coach, alone, including the
            * day-12 date ask and the day-18 displacement, which only somebody
            * holding a budget can answer. The school drawer had the committee
            * and a working picker, and staged no copy at all. Measured on
            * GET /api/outreach/chain, production, 2026-10-16: staged 4,
            * recipients 0, breaksAt step 6. Nothing has ever gone out because
            * nothing was ever addressed.
            *
            * The picker is REUSED rather than reimplemented, and it reports its
            * selection up so the SEQUENCE sends to the people the rep TICKED. */}
          {Array.isArray(dossier.schoolCandidates) && dossier.schoolCandidates.length > 0 && (
            /* SELECT THE SCHOOL FROM THE SYSTEM. Real accounts the resolver
             * refused to choose between — they were always in the payload, and
             * the dossier was discarding them, which left the rep a bare "needs
             * review" and no way to pick. */
            <div className={styles.candidates}>
              <b>Which school is this?</b>
              {dossier.coachReview?.detail && <span>{dossier.coachReview.detail}</span>}
              {dossier.coachReview?.note && <em>{dossier.coachReview.note}</em>}
              <ul>
                {dossier.schoolCandidates.map((cd) => (
                  <li key={cd.id}>
                    <button type="button" className="btn" onClick={() => pickSchool(cd)}>{cd.name}</button>
                    <span>
                      {[cd.kind, cd.state, `${cd.contacts} contact${cd.contacts === 1 ? '' : 's'}`,
                        cd.key || 'no school key'].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {dossier.suggestions === null && dossier.coachReview?.reason === 'people_lookup_failed' && (
            /* ⚠️ null IS NOT ZERO. An unreadable contact search must never
             * render as a school with nobody at it — that is the confident
             * "nobody is here" a rep does not retry. */
            <p className={styles.foot}>
              The contact list could not be read just now, so this is UNKNOWN rather than empty.
              {' '}{dossier.coachReview.note}
            </p>
          )}
          <CoachCandidates championshipId={String(champ.id)} viewKey={candidateView} onChoose={chooseCandidate} selectedContactId={chosenCandidate?.selectedCoach?.contactId} enabled={!demo && facts.ready && !facts.busy}>
          {({result,selectContact})=><>
          {chosenCandidate && <p>Selected coach: {chosenCandidate.person.name}. <button type="button" onClick={()=>selectContact(null)}>Clear coach selection</button></p>}
          <OutreachPicker
            coachEligibility={result}
            onSelectCoach={selectContact}
            selectedCoachContactId={chosenCandidate?.selectedCoach?.contactId}
            embedded
            key={`recipients:${champ.id}:${facts.revision}:${titleBinding}`}
            selectionOnly={!ringEligible}
            championshipId={dossier.championshipId}
            school={dossier.resolvedSchool}
            coach={dossier.coach}
            committee={dossier.committee}
            suggestions={dossier.suggestions}
            reach={dossier.reach}
            sport={c?.sport}
            source="champion-to-close"
            onSelectionChange={onSelectionChange}
          />
          </>}
          </CoachCandidates>
          {initialArtworkProof&&attachmentKey==='requested'&&engineJobs.state==='ready'&&!selectedAttachment&&<p className={styles.gateNote} role="alert">The requested artwork version is unavailable or changed. No substitute was selected. Refresh saved artwork and explicitly choose the exact version before preparing email.</p>}
          {<OutreachArtwork options={attachmentOptions} selected={selectedAttachment} value={attachmentKey==='requested'?(selectedAttachment?.key||'requested'):attachmentKey}
            onChange={setAttachmentKey} onReady={setAttachmentReady} loading={engineJobs.state === 'loading'} context={attachmentContext} error={engineJobs.state === 'error'} onRetry={()=>setEngineRefresh(n=>n+1)} />}
          {chosenCandidate&&selectedAttachment&&<div>
            <button type="button" onClick={openNativeArtworkComposer} disabled={!facts.ready||facts.busy||!copyDossier||!attachmentReady||!senderSignature}>Prepare native email with this artwork</button>
            <p>Review native copy for this exact saved original and {chosenCandidate.person.name}. Current coach identity and your signature are checked again. Nothing is sent.</p>
          </div>}
          {titleRouting.titleTier!=='congrats_tier'&&<label>Campaign purpose<select aria-label="Campaign purpose" value={campaignAngle} onChange={event=>setCampaignAngle(event.target.value)}><option value="">Choose the purpose; no default campaign</option>{OUTREACH_FAMILIES.filter(family=>family.id!=='regional-district-congratulations').map(family=><option key={family.id} value={family.id}>{family.label}</option>)}</select></label>}
          <p role="status">{!sequenceAngle?'Choose a campaign purpose before reviewing its copy.':sequenceRead.state==='ready'?'Campaign copy uses current record evidence. Suggested timing does not authorize a follow-up or send.':sequenceRead.state==='error'?sequenceRead.error:'Checking current campaign evidence; no fallback copy is selected.'}</p>
          {sequenceAngle&&<button type="button" className="btn" onClick={sequenceRead.refresh}>Refresh campaign evidence</button>}
          <OutreachSequence
            key={outboundBinding.current}
            blocked={!facts.ready || facts.busy || !copyDossier}
            dossier={copyDossier}
            sender={sender}
            sequence={familySequence}
            draftNotice={campaignNoteView.current === outboundBinding.current && campaignNote ? campaignNote : platformHistory?.id===dossierId ? platformHistory.unavailable ? 'Platform activity history is unavailable. No first-contact claim can be established.' : `${platformHistory.events.length} outcome reports recorded in this platform. External mailbox history is unknown; legacy reports do not establish which artwork was shared.` : 'Checking recorded platform activity.'}
            recipientMode={(selection.people || []).some(person => person.email) ? 'addressed' : 'unaddressed'}
            recipientNeededKeys={Object.entries(receiptsByView[outboundBinding.current] || {}).filter(([, status]) => status === 'recipient_needed').map(([key]) => key)}
            approvedSurfaces={selectedAttachment && attachmentReady ? 1 : 0}
            attachmentName={selectedAttachment?.output.name || ''}
            draftBusy={draftBusy}
            heldKeys={heldDrafts}
            draftBlocker={initialArtworkProof&&attachmentKey==='requested'&&!selectedAttachment?'The requested exact artwork version is not available; no substitute may be drafted.':!copyDossier ? 'Reload the complete current championship facts before reviewing outreach copy.' : attachmentOptions.length > 1 && attachmentKey === 'auto' ? 'Choose the artwork to include.'
              : selectedAttachment && !attachmentReady ? 'Preparing the selected artwork preview.' : ''}
            sentKeys={Object.keys(receiptsByView[outboundBinding.current] || {})}
            onSend={draftOne}
            onConfirmSent={confirmSent}
            reportedOutcomes={Object.values(reportedByView[reportDisplayBinding.current]||{})}
            gmail={gmail}
          />
          </> : <p className={styles.foot} role="status">Loading the championship dossier and contact choices…</p>}
        </section>

        <p className={styles.foot}>{ringEligible ? 'Quote approval happens in the Pricing Engine. Staging creates a draft; it does not send email. The ring is built in the Artwork Studio.' : 'Staging creates a draft; it does not send email. Regional and district awards use congratulations correspondence without ring artwork. Unclear award levels stay held for review.'}</p>
        {sequenceComposer?.binding===(sequenceComposer?.mode==='native-artwork'?nativeArtworkBinding.current:outboundBinding.current)&&<SavedArtworkComposer proof={sequenceComposer.proof} sequenceSelection={sequenceComposer.selection} recipientSelection={sequenceComposer.recipientSelection} onClose={()=>setSequenceComposer(null)} onSaved={receipt=>{
          if(sequenceComposer.binding!==(sequenceComposer.mode==='native-artwork'?nativeArtworkBinding.current:outboundBinding.current))return
          const reference=outcomeDraftReference(receipt,sequenceComposer.proof)
          if(!reference)throw Error('The exact draft reference could not be retained for outcome reporting.')
          recordDossierReceipt(sequenceComposer.binding,sequenceComposer.variant,'addressed')
          outcomeDrafts.current.set(JSON.stringify([sequenceComposer.binding,sequenceComposer.variant]),reference)
          setCampaignNote('The reviewed artwork email has a confirmed draft-creation receipt in your own mailbox. Nothing was sent. Record a send only after you actually send it.')
        }}/>} 
      </aside>
    </div>
  )
}


